<script setup lang="ts">
import { computed, markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import {
    PauseIcon,
    PlayIcon,
    PlayCircleIcon,
    XMarkIcon,
    BackwardIcon,
    ForwardIcon,
    QueueListIcon,
    ArrowPathRoundedSquareIcon,
    ArrowsRightLeftIcon,
    EllipsisHorizontalIcon,
} from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CoverArtTile from '@/components/CoverArtTile.vue';
import QueueInspectorContent from '@/components/QueueInspectorContent.vue';
import { useAudioPlaybackStore } from '@/stores/playerStore';
import { useInspectorStore } from '@/stores/inspectorStore';
import { usePermissions } from '@/composables/usePermissions';
import { postRerunAudioFileVerification } from '@/composables/api/useAudioFileIssuesApi';
import { useAltKeyHeld } from '@/composables/useAltKeyHeld';
import { useMediaSession } from '@/composables/useMediaSession';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import {
    decodeUrlToWaveformPeaks,
} from '@/utils/audioWaveformPeaks';

/**
 * Reusable diagnostics audio player.
 *
 * Renders as a normal block element — the parent decides positioning
 * (e.g. sticky footer inside an inspector panel/modal). The component
 * is hidden via Transition until the playback store is `visible`.
 *
 * Progress shows a **decoded waveform** for MP3 / blob URLs once audio is
 * ready and decode succeeds. There is **no synthetic placeholder** while
 * loading or for HLS — the strip uses a simple linear bar until real peaks
 * exist (or indefinitely when no decode is possible). Peaks decode at high
 * bucket count and draw one column per CSS pixel when present.
 *
 * The template has multiple root nodes (the hidden `<audio>` element plus a
 * `<Transition>`), so attribute inheritance is disabled and `$attrs` are
 * forwarded to the visible bar so callers can pass through `class` etc.
 */
defineOptions({ inheritAttrs: false });

/** Matches waveform stack: scrubber `h-8` + `gap-1` + duration row `leading-4`. */
const audioPlayerCoverSidePx = 52;

const playback = useAudioPlaybackStore();
// Mirror the bar onto the OS media controls (lock screen, media keys,
// Chrome's media hub): title/artist/album/cover, the scrubber, and
// Play/Pause/Next/Previous routed back into the store. Lives here because this
// component owns the <audio> element, and the OS-facing side should come and
// go with it.
useMediaSession(playback);

const audioRef = ref<HTMLAudioElement | null>(null);

const progressBarEl = ref<HTMLElement | null>(null);
const waveformCanvasRef = ref<HTMLCanvasElement | null>(null);
const playedColorRef = ref<HTMLElement | null>(null);
const unplayedColorRef = ref<HTMLElement | null>(null);

const peaks = shallowRef<number[] | null>(null);
let decodeAbortController: AbortController | null = null;
let drawRaf = 0;
let resizeObserver: ResizeObserver | null = null;

function onPointerMove(ev: PointerEvent) {
    applySeekFromPointer(ev);
}

function applySeekFromPointer(ev: { clientX: number }) {
    const bar = progressBarEl.value;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const fraction = (ev.clientX - rect.left) / rect.width;

    playback.seekToFraction(fraction);
}

function onPointerUp() {
    window.removeEventListener('pointermove', onPointerMove);
}

function onPointerDown(ev: MouseEvent | PointerEvent) {
    ev.preventDefault();

    applySeekFromPointer(ev);

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
}

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const s = Math.floor(seconds % 60);
    const m = Math.floor(seconds / 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const {
    visible,
    trackTitle,
    trackArtist,
    trackAlbum,
    trackCodeUpper,
    trackEntityId,
    trackRecordId,
    trackArtistId,
    trackAlbumId,
    coverArtUrl,
    activeDescriptorSource,
    mode,
    downloadFraction,
    currentTimeSeconds,
    durationSeconds,
    isPaused,
    errorMessage,
    hasQueue,
    canGoNext,
    canGoPrevious,
    repeatMode,
    shuffleEnabled,
    currentEntrySupportsDeepCheck,
    displayTitle,
} = storeToRefs(playback);

const inspectorStore = useInspectorStore();
const router = useRouter();
const { canAccessPage } = usePermissions();

const repeatLabel = computed(() => {
    if (repeatMode.value === 'all') return 'Repeat: queue';
    if (repeatMode.value === 'one') return 'Repeat: one track';

    return 'Repeat: off';
});

const queueInspectorComponent = markRaw(QueueInspectorContent);

/** The queue view is a toggle, not a stack-grower. */
const queueViewIsOpen = computed(
    () => inspectorStore.isOpen && inspectorStore.component === queueInspectorComponent
);

function toggleQueueView(): void {
    if (queueViewIsOpen.value) {
        // `pop()` no-ops at depth 1, so a queue opened as the only view would
        // leave the button looking dead without this branch.
        if (inspectorStore.canGoBack) {
            inspectorStore.pop();
        } else {
            inspectorStore.close();
        }

        return;
    }

    inspectorStore.push('Queue', queueInspectorComponent);
}

/**
 * Whether to offer "View in Audio File Issues" beside a playback error.
 *
 * Needs BOTH the page grant and a live-track entry: the deep-check endpoint sits
 * inside the Audio-File-Issues page-granted route group and that gate is its
 * only authorization, so offering the link without the grant would hand the user
 * a 403. And Audio File Issues is keyed on a LiveTrack code, so there is nothing
 * to check a DDEX track against.
 */
const canDeepCheckCurrent = computed(
    () =>
        errorMessage.value !== null &&
        currentEntrySupportsDeepCheck.value &&
        trackCodeUpper.value !== null &&
        canAccessPage('Audio File Issues')
);

const isDeepChecking = ref(false);

async function openInAudioFileIssues(): Promise<void> {
    const code = trackCodeUpper.value;
    if (!code || isDeepChecking.value) {
        return;
    }

    isDeepChecking.value = true;
    try {
        // Fire the deep check BEFORE navigating: the endpoint resolves the
        // LiveTrack by code rather than by an existing issue row, so this is
        // what puts a track onto the Audio File Issues page if it isn't already
        // there — and refreshes it if it is.
        await postRerunAudioFileVerification(code);
    } catch {
        // Navigating is still useful even if queueing the re-check failed; the
        // page surfaces its own state.
    } finally {
        isDeepChecking.value = false;
    }

    await router.push({ name: 'audio-file-issues-detail', params: { code } });
}

const altHeld = useAltKeyHeld();

/**
 * The three identity lines are links whenever the call site that started
 * playback told the store which records they stand for.
 *
 * A line with no id behind it (diagnostics playback, an unresolved player
 * check-in, a source that carries only names) renders as the same plain text it
 * always did — the layout is identical either way.
 *
 * `pushWhenInspectorOpen` is what keeps this consistent with the other half of
 * this branch: the bar is outside the inspector but visible below the center
 * peek, so replacing the stack from here would strand a user several levels deep
 * with a dead Back button.
 */
const { canOpen, openRecord } = useOpenRecordInInspector();

/**
 * Both halves are required, and the NAME is the half that is easy to forget.
 *
 * `readNestedDisplayString` returns null for a blank `display_string` while the
 * id off that same relation reads fine, which produced an underlined, clickable
 * "Unknown artist" — a link whose text names no record. A line that cannot say
 * what it opens stays plain text.
 */
/** Link decoration shared by whichever of the three lines are openable. */
const identityLinkClass =
    'hover:text-primary focus-visible:text-primary block max-w-full cursor-pointer text-left underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none';


const trackRecordIsOpenable = computed(
    () => trackRecordId.value != null && displayTitle.value !== '' && canOpen(trackEntityId.value),
);
const artistRecordIsOpenable = computed(
    () => trackArtistId.value != null && trackArtist.value != null && canOpen('Artist'),
);
const albumRecordIsOpenable = computed(
    () => trackAlbumId.value != null && trackAlbum.value != null && canOpen('Album'),
);

function openTrackRecord(): void {
    if (trackRecordIsOpenable.value) {
        openRecord(trackEntityId.value as string, trackRecordId.value as number | string, {
            pushWhenInspectorOpen: true,
        });
    }
}

function openArtistRecord(): void {
    if (artistRecordIsOpenable.value) {
        openRecord('Artist', trackArtistId.value as number | string, {
            pushWhenInspectorOpen: true,
        });
    }
}

function openAlbumRecord(): void {
    if (albumRecordIsOpenable.value) {
        openRecord('Album', trackAlbumId.value as number | string, {
            pushWhenInspectorOpen: true,
        });
    }
}

// Same Option-modifier as the cover art: while a live MP3 is loaded, holding
// Option turns the play control into "play HLS" and restarts the current track
// as its HLS stream. Only live MP3 can switch — DDEX / manual / backup sources
// have no HLS variant.
const canSwitchToHls = computed(() => activeDescriptorSource.value === 'live_mp3');
const optionHls = computed(() => altHeld.value && canSwitchToHls.value);

// Each HLS switch tears down and rebuilds the stream from 0:00, so a second
// Option-click landing before the first load settles would race two teardowns.
const isSwitchingToHls = ref(false);

async function onPlayButton(): Promise<void> {
    if (optionHls.value && trackCodeUpper.value) {
        if (isSwitchingToHls.value) {
            return;
        }
        isSwitchingToHls.value = true;
        try {
            await playback.loadAndPlay({
                code: trackCodeUpper.value,
                title: trackTitle.value,
                artist: trackArtist.value,
                album: trackAlbum.value,
                coverUrl: coverArtUrl.value,
                // Inside a real queue the store recognises this as a source
                // switch and keeps the existing entry, links and all. A one-off
                // play is a queue of ONE, which that check excludes, so it
                // rebuilds the entry from these options — and without the
                // records the bar's lines would quietly stop being links after
                // an Option-click.
                trackEntityId: trackEntityId.value,
                trackRecordId: trackRecordId.value,
                artistId: trackArtistId.value,
                albumId: trackAlbumId.value,
                source: 'live_hls',
            });
        } catch {
            // The player store records errorMessage; the bar surfaces it.
        } finally {
            isSwitchingToHls.value = false;
        }
        return;
    }
    await playback.togglePlay();
}

watch(audioRef, (el) => {
    playback.attachAudio(el ?? null);

    return;
});

watch(visible, (v) => {
    if (v && audioRef.value) {
        playback.attachAudio(audioRef.value);
    }
    if (!v) {
        peaks.value = null;
        decodeAbortController?.abort();
        decodeAbortController = null;
    }
});

function onTimeUpdate() {
    playback.syncTimelineFromDom();
}

function onLoadedMetadata() {
    playback.syncTimelineFromDom();
}

function onAudioLoadedData() {
    void refreshWaveformPeaks();
}

const canScrubFull = () => mode.value === 'mp3_ready' && durationSeconds.value > 0;
const showDownloadBar = () =>
    mode.value === 'mp3_loading' || (mode.value === 'mp3_ready' && downloadFraction.value < 1);

function playbackFillWidth() {
    if (!durationSeconds.value) return 0;
    return Math.min(100, (currentTimeSeconds.value / durationSeconds.value) * 100);
}

/**
 * The same fraction as {@link playbackFillWidth}, as a reactive value.
 *
 * That one is called from the canvas draw loop, which re-runs on a watcher, so
 * it is a plain function. The phone bar's top-edge progress line is a DOM style
 * binding and needs a computed to re-render at all.
 */
const playbackPercent = computed(() => playbackFillWidth());

/**
 * "0:41 / 4:01" — elapsed and duration on ONE line, for the phone bar.
 *
 * Two separate spans in a `justify-between` row is what produced the reported
 * "0:414:01": the waveform column was squeezed to nothing, so `justify-between`
 * had no space to distribute and the two times rendered flush against each
 * other. A single string cannot collide with itself.
 */
const compactTimeLabel = computed(
    () => `${formatTime(currentTimeSeconds.value)} / ${formatTime(durationSeconds.value)}`
);

async function refreshWaveformPeaks() {
    decodeAbortController?.abort();
    decodeAbortController = new AbortController();
    const signal = decodeAbortController.signal;

    if (!visible.value) {
        peaks.value = null;
        return;
    }

    const m = mode.value;

    if (m === 'hls' || m === 'mp3_loading') {
        peaks.value = null;
        scheduleWaveformDraw();
        return;
    }

    if (m === 'mp3_ready') {
        const audio = audioRef.value;
        const url = audio?.currentSrc || audio?.src;
        if (!url) {
            peaks.value = null;
            scheduleWaveformDraw();
            return;
        }
        const decoded = await decodeUrlToWaveformPeaks(url, signal);
        if (signal.aborted) {
            return;
        }
        peaks.value = decoded;
        scheduleWaveformDraw();
        return;
    }

    peaks.value = null;
    scheduleWaveformDraw();
}

function scheduleWaveformDraw() {
    cancelAnimationFrame(drawRaf);
    drawRaf = requestAnimationFrame(() => {
        drawWaveform();
    });
}

function readCssColor(el: HTMLElement | null, fallback: string): string {
    if (!el) return fallback;
    const c = getComputedStyle(el).color;
    return c && c !== 'rgba(0, 0, 0, 0)' ? c : fallback;
}

function drawWaveform() {
    const canvas = waveformCanvasRef.value;
    const wrap = progressBarEl.value;
    if (!canvas || !wrap) {
        return;
    }

    const rect = wrap.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(24, Math.floor(rect.height));
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const playedFrac = playbackFillWidth() / 100;
    const peakList = peaks.value;
    const playedRgb = readCssColor(playedColorRef.value, 'hsl(222.2 47.4% 11.2%)');
    const unplayedRgb = readCssColor(unplayedColorRef.value, 'hsl(215.4 16.3% 46.9%)');

    if (peakList?.length) {
        const barCount = peakList.length;
        const mid = h / 2;
        const usableH = h - 6;
        // One column per CSS pixel: max-aggregate peaks so high-res decodes stay
        // sharp on wide strips instead of fat blocks.
        for (let col = 0; col < w; col++) {
            const t0 = col / w;
            const t1 = (col + 1) / w;
            const i0 = Math.floor(t0 * barCount);
            const i1 = Math.min(barCount, Math.max(i0 + 1, Math.ceil(t1 * barCount)));
            let amp = 0;
            for (let j = i0; j < i1; j++) {
                const v = peakList[j] ?? 0;
                if (v > amp) {
                    amp = v;
                }
            }
            const barHeight = Math.max(1.5, amp * usableH);
            const y = mid - barHeight / 2;
            const isPlayed = (col + 0.5) / w <= playedFrac;
            ctx.fillStyle = isPlayed ? playedRgb : unplayedRgb;
            ctx.fillRect(col, y, 1, barHeight);
        }
    } else {
        // No decoded peaks yet (loading, HLS, decode failure) — linear bar only, no fake waveform.
        const mid = h / 2;
        const trackH = Math.max(4, h - 10);
        const y = mid - trackH / 2;
        ctx.fillStyle = unplayedRgb;
        ctx.globalAlpha = 0.35;
        ctx.fillRect(0, y, w, trackH);
        ctx.globalAlpha = 1;
        ctx.fillStyle = playedRgb;
        ctx.fillRect(0, y, Math.max(0, w * playedFrac), trackH);
    }

    if (showDownloadBar()) {
        const frac = downloadFraction.value;
        ctx.fillStyle = 'rgba(128, 128, 128, 0.18)';
        ctx.fillRect(0, 0, w * frac, h);
    }
}

watch(
    [visible, mode, trackCodeUpper, trackTitle, () => audioRef.value?.src, () => audioRef.value?.currentSrc],
    () => {
        void refreshWaveformPeaks();
    },
    { flush: 'post' },
);

watch(
    [currentTimeSeconds, durationSeconds, downloadFraction, mode, peaks],
    () => {
        scheduleWaveformDraw();
    },
    { flush: 'post' },
);

onBeforeUnmount(() => {
    decodeAbortController?.abort();
    resizeObserver?.disconnect();
    cancelAnimationFrame(drawRaf);
    playback.attachAudio(null);
});

onMounted(() => {
    playback.attachAudio(audioRef.value);
    // Bring back the queue from the last session, paused and with no audio
    // loaded. Called here rather than at store construction so it is explicit
    // and skippable, and so it is the obvious seam if this ever becomes a
    // server-side cross-device queue.
    playback.restoreFromPersistence();
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
            scheduleWaveformDraw();
        });
        watch(
            progressBarEl,
            (el, prev) => {
                if (prev) {
                    resizeObserver?.unobserve(prev);
                }
                if (el) {
                    resizeObserver?.observe(el);
                }
            },
            { immediate: true },
        );
    }
});
</script>

<template>
    <audio
        ref="audioRef"
        class="hidden"
        crossorigin="anonymous"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @loadeddata="onAudioLoadedData"
        @play="playback.syncTimelineFromDom()"
        @pause="playback.syncTimelineFromDom()"
        @durationchange="playback.syncTimelineFromDom()"
        @seeked="playback.syncTimelineFromDom()"
        @ended="playback.onTrackEnded()"
        @error="playback.onAudioElementError()"
    />

    <Transition
        enter-active-class="transition-transform duration-200 ease-out"
        leave-active-class="transition-transform duration-150 ease-in"
        enter-from-class="translate-y-full"
        enter-to-class="translate-y-0"
        leave-from-class="translate-y-0"
        leave-to-class="translate-y-full"
    >
        <div
            v-if="visible"
            v-bind="$attrs"
            data-testid="global-audio-player"
            class="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 @container relative w-full border-t px-4 py-2"
        >
            <!--
              Narrow-bar progress, on the bar's own top edge.
              Below `@3xl` the waveform is hidden (see the scrubber column):
              there is not enough width to draw peaks that anyone could read, and
              the row it lived in was what squeezed the two time labels together.
              This line is the whole progress affordance at that width.

              A CONTAINER query, not a viewport one. The first version of this
              used `sm:` and looked right on a phone, but the bar is not as wide
              as the window — the sidebar takes ~250px of it. At an 811px
              viewport the bar had ~560px, which is under what seven buttons plus
              a scrubber need, so the desktop layout still rendered and the
              waveform measured ONE pixel wide with the two time labels flush
              against each other: the reported "0:414:01", above the breakpoint
              that was supposed to prevent it. What matters is how wide this
              element is, which is exactly what `@container` asks.
            -->
            <div
                class="bg-muted absolute inset-x-0 top-0 h-[3px] @3xl:hidden"
                aria-hidden="true"
                data-testid="player-edge-progress"
            >
                <div class="bg-primary h-full" :style="{ width: `${playbackPercent}%` }" />
            </div>

            <!--
              Identity (cover + title) takes a FIXED width at @3xl; playback (play +
              waveform + close) uses flex-1 so extra width goes into the scrubber
              rather than dead space between the blocks.

              Fixed, not content-width, because the transport starts wherever the
              identity ends. With `basis-auto` that edge was the track's own metadata,
              so every control after it moved on every track change — measured across
              eight seeded tracks the identity ran from 159px to 269px, sliding
              Play/Pause 110px with the user doing nothing but letting the queue
              advance. Play is the most-aimed-at control in the app and this bar is
              persistent, so a stable position is worth more than showing a long title
              in full: the three identity lines already truncate, and now they do it at
              a width that does not move.

              16rem was chosen from that measurement — it clears six of the eight
              outright and clips only the two longest. At the @3xl container minimum it
              still leaves room for the transport's fixed controls plus a scrubber.
            -->
            <div class="mx-auto flex w-full flex-wrap items-center gap-x-3 gap-y-2 @3xl:items-start">
                <div class="flex min-w-0 flex-1 items-center gap-3 @3xl:grow-0 @3xl:shrink-0 @3xl:basis-64 @3xl:items-start">
                    <CoverArtTile
                        :src="coverArtUrl"
                        :alt="trackTitle ? `Cover art for ${trackTitle}` : 'Cover art'"
                        :size="audioPlayerCoverSidePx"
                        :preview-size="320"
                        class="shrink-0 self-center @3xl:self-start"
                    />
                    <!--
                      Three-line identity matching the History Inspector / AudioFileIssueCard:
                      display string (title + remix), artist, album. The track code + audio
                      source slug (e.g. "DDEX audio") were intentionally dropped per product
                      direction — the cover and the title carry the identity, and which audio
                      stream is playing is implicit from the call site. `trackArtist` /
                      `trackAlbum` are populated by every play call site; nullables fall back
                      to "Unknown …" so the line height stays constant rather than collapsing.
                    -->
                    <!--
                      Each line is ONE element that becomes a <button> when the
                      store knows the record behind it (see
                      `trackRecordIsOpenable` and friends) and stays a <p>
                      otherwise. Written as a pair of elements the text and its
                      "Unknown …" fallback existed twice per line, so changing
                      one copy silently gave linked and unlinked tracks
                      different wording.

                      The aria-label names the ACTION, because the visible text
                      is only the record's name — a screen reader would
                      otherwise announce "Various Artists, button" with nothing
                      to say what pressing it does, three times over.
                    -->
                    <div class="min-w-0">
                        <component
                            :is="trackRecordIsOpenable ? 'button' : 'p'"
                            :type="trackRecordIsOpenable ? 'button' : undefined"
                            :class="[
                                'truncate text-sm font-medium',
                                trackRecordIsOpenable ? identityLinkClass : '',
                            ]"
                            :aria-label="trackRecordIsOpenable ? `Open track ${displayTitle}` : undefined"
                            :data-testid="trackRecordIsOpenable ? 'audio-player-open-track' : undefined"
                            @click="trackRecordIsOpenable && openTrackRecord()"
                        >
                            {{ displayTitle || 'Diagnostics playback' }}
                        </component>
                        <!--
                          The artist line carries the times on a narrow bar, where
                          the scrubber's own labels are hidden. One string, so the
                          elapsed and the duration cannot run together the way two
                          `justify-between` spans did in a zero-width column.

                          `truncate` sits on the artist ALONE, never on this row.
                          With the ellipsis on the shared parent the clock was the
                          part that got cut ("Alison Upton · 0:…"), which is the
                          same failure by a different mechanism. The artist gives
                          way; the clock does not.

                          The name is a `span` rather than the `p` it is elsewhere:
                          it now sits inside this `p`, and a nested paragraph is
                          invalid HTML that browsers silently unnest.
                        -->
                        <p class="text-foreground flex items-baseline text-xs leading-snug">
                            <component
                                :is="artistRecordIsOpenable ? 'button' : 'span'"
                                :type="artistRecordIsOpenable ? 'button' : undefined"
                                :class="[
                                    'min-w-0 truncate',
                                    artistRecordIsOpenable ? identityLinkClass : '',
                                ]"
                                :aria-label="artistRecordIsOpenable ? `Open artist ${trackArtist}` : undefined"
                                :data-testid="artistRecordIsOpenable ? 'audio-player-open-artist' : undefined"
                                @click="artistRecordIsOpenable && openArtistRecord()"
                            >
                                {{ trackArtist ?? 'Unknown artist' }}
                            </component>
                            <span
                                class="text-muted-foreground shrink-0 whitespace-nowrap tabular-nums @3xl:hidden"
                                data-testid="player-compact-time"
                            >
                                &nbsp;· {{ compactTimeLabel }}
                            </span>
                        </p>
                        <!-- Album is the first thing to go when width is short. -->
                        <component
                            :is="albumRecordIsOpenable ? 'button' : 'p'"
                            :type="albumRecordIsOpenable ? 'button' : undefined"
                            :class="[
                                'text-muted-foreground hidden truncate text-xs leading-snug @3xl:block',
                                albumRecordIsOpenable ? identityLinkClass : '',
                            ]"
                            :aria-label="albumRecordIsOpenable ? `Open album ${trackAlbum}` : undefined"
                            :data-testid="albumRecordIsOpenable ? 'audio-player-open-album' : undefined"
                            @click="albumRecordIsOpenable && openAlbumRecord()"
                        >
                            {{ trackAlbum ?? 'Unknown album' }}
                        </component>
                    </div>
                </div>

                <div class="flex min-w-0 items-center gap-1 @3xl:flex-1 @3xl:basis-0 @3xl:items-start @3xl:gap-3">
                    <!-- Transport. Every control is null-safe against an empty
                         queue: the bar renders for one-off diagnostics playback
                         too, where there is nothing to skip to.

                         Below `sm` only play, next and queue stay inline; the
                         rest move into the overflow menu at the end of this row.
                         Seven icon buttons plus a scrubber do not fit in 375px,
                         which is what crushed this bar in the first place. -->
                    <Button
                        v-if="hasQueue"
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="hidden shrink-0 @3xl:inline-flex"
                        aria-label="Previous track"
                        data-testid="player-previous"
                        :disabled="!canGoPrevious"
                        @click="playback.playPrevious()"
                    >
                        <BackwardIcon class="h-4 w-4" />
                    </Button>

                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        class="shrink-0"
                        :aria-label="optionHls ? 'Play HLS' : 'Play or pause'"
                        :disabled="!trackCodeUpper"
                        @click="onPlayButton()"
                    >
                        <PlayCircleIcon v-if="optionHls" class="h-4 w-4" />
                        <PauseIcon v-else-if="!isPaused" class="h-4 w-4" />
                        <PlayIcon v-else class="h-4 w-4" />
                    </Button>

                    <Button
                        v-if="hasQueue"
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="shrink-0"
                        aria-label="Next track"
                        data-testid="player-next"
                        :disabled="!canGoNext"
                        @click="playback.playNext()"
                    >
                        <ForwardIcon class="h-4 w-4" />
                    </Button>

                    <!-- The scrubber column collapses entirely below `sm` (the
                         top-edge line replaces it), except for the error, which
                         must never be the thing that gets dropped for space. -->
                    <div class="flex min-w-0 flex-col gap-1 @3xl:flex-1">
                      <div class="hidden flex-col gap-1 @3xl:flex">
                        <div
                            ref="progressBarEl"
                            class="bg-muted relative h-8 w-full cursor-pointer overflow-hidden rounded-md"
                            role="slider"
                            :aria-valuenow="Math.round(currentTimeSeconds)"
                            :aria-valuemax="Math.round(durationSeconds)"
                            aria-orientation="horizontal"
                            :aria-disabled="!canScrubFull() && mode !== 'hls'"
                        >
                            <!-- Off-screen probes so `getComputedStyle` picks up theme colors. -->
                            <span
                                ref="playedColorRef"
                                class="pointer-events-none absolute left-[-9999px] top-0 text-primary"
                                aria-hidden="true"
                            >.</span>
                            <span
                                ref="unplayedColorRef"
                                class="pointer-events-none absolute left-[-9999px] top-0 text-muted-foreground/55"
                                aria-hidden="true"
                            >.</span>
                            <canvas
                                ref="waveformCanvasRef"
                                class="pointer-events-none absolute inset-0 block h-full w-full"
                                data-testid="audio-player-waveform"
                                aria-hidden="true"
                            />
                            <div
                                class="absolute inset-0 z-[1]"
                                @pointerdown="onPointerDown"
                            />
                        </div>
                        <div class="text-muted-foreground flex justify-between text-[11px] leading-4">
                            <span>{{ formatTime(currentTimeSeconds) }}</span>
                            <span>{{ formatTime(durationSeconds) }}</span>
                        </div>
                      </div>
                        <!-- A track that won't play is a signal about our
                             library, not a consumer-app hiccup: say so, and
                             offer the one-click path to diagnosing it. -->
                        <p v-if="errorMessage" class="text-destructive flex items-center gap-2 text-xs">
                            <span class="truncate">{{ errorMessage }}</span>
                            <button
                                v-if="canDeepCheckCurrent"
                                type="button"
                                class="shrink-0 underline underline-offset-2 disabled:opacity-60"
                                data-testid="player-deep-check-link"
                                :disabled="isDeepChecking"
                                @click="openInAudioFileIssues()"
                            >
                                View in Audio File Issues
                            </button>
                        </p>
                    </div>

                    <Button
                        v-if="hasQueue"
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="hidden shrink-0 @3xl:inline-flex"
                        :aria-label="shuffleEnabled ? 'Shuffle: on' : 'Shuffle: off'"
                        data-testid="player-shuffle"
                        :class="shuffleEnabled ? 'text-primary' : ''"
                        @click="playback.setShuffle(!shuffleEnabled)"
                    >
                        <ArrowsRightLeftIcon class="h-4 w-4" />
                    </Button>

                    <Button
                        v-if="hasQueue"
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="hidden shrink-0 @3xl:inline-flex"
                        :aria-label="repeatLabel"
                        data-testid="player-repeat"
                        :class="repeatMode !== 'off' ? 'text-primary' : ''"
                        @click="playback.cycleRepeatMode()"
                    >
                        <ArrowPathRoundedSquareIcon class="h-4 w-4" />
                        <span v-if="repeatMode === 'one'" class="ml-0.5 text-[10px] font-bold">1</span>
                    </Button>

                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="shrink-0"
                        :aria-label="queueViewIsOpen ? 'Hide queue' : 'Show queue'"
                        data-testid="player-queue-button"
                        :class="queueViewIsOpen ? 'text-primary' : ''"
                        @click="toggleQueueView()"
                    >
                        <QueueListIcon class="h-5 w-5" />
                    </Button>

                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="hidden shrink-0 @3xl:inline-flex"
                        aria-label="Close player"
                        @click="playback.close()"
                    >
                        <XMarkIcon class="h-5 w-5" />
                    </Button>

                    <!--
                      Phone overflow. Holds exactly what the row above drops
                      below `sm` — previous, shuffle, repeat, close — so nothing
                      the bar offers on a desktop becomes unreachable on a phone.
                      Labels rather than bare icons: a menu row has the width for
                      them, and "Shuffle: on" says the state as well as the action.
                    -->
                    <DropdownMenu>
                        <DropdownMenuTrigger as-child>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                class="shrink-0 @3xl:hidden"
                                aria-label="More player controls"
                                data-testid="player-overflow"
                            >
                                <EllipsisHorizontalIcon class="h-5 w-5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" side="top" class="min-w-[11rem]">
                            <DropdownMenuItem
                                v-if="hasQueue"
                                :disabled="!canGoPrevious"
                                data-testid="player-overflow-previous"
                                @click="playback.playPrevious()"
                            >
                                <BackwardIcon class="mr-2 h-4 w-4" />
                                Previous track
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                v-if="hasQueue"
                                data-testid="player-overflow-shuffle"
                                @click="playback.setShuffle(!shuffleEnabled)"
                            >
                                <ArrowsRightLeftIcon
                                    class="mr-2 h-4 w-4"
                                    :class="shuffleEnabled ? 'text-primary' : ''"
                                />
                                {{ shuffleEnabled ? 'Shuffle: on' : 'Shuffle: off' }}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                v-if="hasQueue"
                                data-testid="player-overflow-repeat"
                                @click="playback.cycleRepeatMode()"
                            >
                                <ArrowPathRoundedSquareIcon
                                    class="mr-2 h-4 w-4"
                                    :class="repeatMode !== 'off' ? 'text-primary' : ''"
                                />
                                {{ repeatLabel }}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="player-overflow-close"
                                @click="playback.close()"
                            >
                                <XMarkIcon class="mr-2 h-4 w-4" />
                                Close player
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
    </Transition>
</template>
